/**
 * Service Management Page
 * =======================
 * 
 * CRUD operations for services - create, edit, delete, manage staff assignments.
 */

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  Users,
  PoundSterling,
  Calendar,
  Wrench,
  MessageCircle,
  Settings,
  Truck,
  ArrowLeft,
  LayoutGrid,
  Check,
  X,
  Image,
  RefreshCw,
  Package,
  Repeat,
  Gift,
  Percent,
  ShoppingCart,
} from "lucide-react";

const CATEGORY_ICONS = {
  repair: Wrench,
  consultation: MessageCircle,
  maintenance: Settings,
  delivery: Truck,
};

const CATEGORY_COLORS = {
  repair: "bg-red-500/20 text-red-400 border-red-500/30",
  consultation: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  maintenance: "bg-green-500/20 text-green-400 border-green-500/30",
  delivery: "bg-amber-500/20 text-amber-400 border-amber-500/30",
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const ServicesPage = () => {
  const { token } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState("services"); // services, bundles, recurring
  
  // Data state
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [recurringBookings, setRecurringBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  // Modal state
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingService, setDeletingService] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "consultation",
    duration_minutes: 30,
    base_price: 0,
    deposit_percent: 20,
    staff_ids: [],
    is_recurring: false,
    recurring_interval_days: null,
    max_advance_days: 30,
    buffer_minutes: 15,
    availability: DAYS.map((_, i) => ({
      day: i,
      start_time: i < 5 ? "09:00" : i === 5 ? "10:00" : "00:00",
      end_time: i < 5 ? "17:00" : i === 5 ? "14:00" : "00:00",
      enabled: i < 6,
    })),
  });
  const [saving, setSaving] = useState(false);

  // Fetch data
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/services/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Seed demo services if empty
  const seedDemoServices = async () => {
    try {
      const res = await fetch(`${API}/services/seed-demo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.seeded > 0) {
          toast.success(`${data.seeded} demo services added!`);
          fetchServices();
        }
      }
    } catch (error) {
      console.error("Error seeding services:", error);
    }
  };

  const fetchServices = useCallback(async () => {
    try {
      let url = `${API}/services?limit=100`;
      if (selectedCategory && selectedCategory !== "all") {
        url += `&category_id=${selectedCategory}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setServices(data);
        // Auto-seed if no services
        if (data.length === 0) {
          seedDemoServices();
        }
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory, searchQuery]);

  const fetchStaff = async () => {
    try {
      const res = await fetch(`${API}/pos/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStaff(data);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  // Fetch bundles
  const fetchBundles = async () => {
    try {
      const res = await fetch(`${API}/services/bundles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBundles(data.bundles || []);
      }
    } catch (error) {
      console.error("Error fetching bundles:", error);
    }
  };

  // Fetch recurring bookings
  const fetchRecurring = async () => {
    try {
      const res = await fetch(`${API}/services/recurring`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRecurringBookings(data.recurring_bookings || []);
      }
    } catch (error) {
      console.error("Error fetching recurring:", error);
    }
  };

  // Purchase bundle
  const purchaseBundle = async (bundleId) => {
    try {
      const res = await fetch(`${API}/services/bundles/${bundleId}/purchase`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ customer_id: "demo_customer" })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Bundle purchased! ${data.total_sessions || 0} sessions available`);
        fetchBundles();
      } else {
        toast.error("Failed to purchase bundle");
      }
    } catch (error) {
      toast.error("Error purchasing bundle");
    }
  };

  // Cancel recurring booking
  const cancelRecurring = async (recurringId) => {
    try {
      const res = await fetch(`${API}/services/recurring/${recurringId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Recurring booking cancelled");
        fetchRecurring();
      }
    } catch (error) {
      toast.error("Error cancelling recurring booking");
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchStaff();
    fetchBundles();
    fetchRecurring();
  }, [token]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Form handlers
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category_id: "consultation",
      duration_minutes: 30,
      base_price: 0,
      deposit_percent: 20,
      staff_ids: [],
      is_recurring: false,
      recurring_interval_days: null,
      max_advance_days: 30,
      buffer_minutes: 15,
      availability: DAYS.map((_, i) => ({
        day: i,
        start_time: i < 5 ? "09:00" : i === 5 ? "10:00" : "00:00",
        end_time: i < 5 ? "17:00" : i === 5 ? "14:00" : "00:00",
        enabled: i < 6,
      })),
    });
    setEditingService(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowServiceModal(true);
  };

  const openEditModal = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      category_id: service.category_id,
      duration_minutes: service.duration_minutes,
      base_price: service.base_price / 100,
      deposit_percent: service.deposit_percent,
      staff_ids: service.staff_ids || [],
      is_recurring: service.is_recurring,
      recurring_interval_days: service.recurring_interval_days,
      max_advance_days: service.max_advance_days,
      buffer_minutes: service.buffer_minutes,
      availability: service.availability || DAYS.map((_, i) => ({
        day: i,
        start_time: "09:00",
        end_time: "17:00",
        enabled: i < 5,
      })),
    });
    setShowServiceModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Service name is required");
      return;
    }
    
    if (formData.duration_minutes < 15) {
      toast.error("Duration must be at least 15 minutes");
      return;
    }
    
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        base_price: Math.round(formData.base_price * 100),
      };
      
      const url = editingService
        ? `${API}/services/${editingService.service_id}`
        : `${API}/services`;
      
      const method = editingService ? "PUT" : "POST";
      
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (res.ok) {
        toast.success(editingService ? "Service updated" : "Service created");
        setShowServiceModal(false);
        resetForm();
        fetchServices();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save service");
      }
    } catch (error) {
      console.error("Error saving service:", error);
      toast.error("Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingService) return;
    
    try {
      const res = await fetch(`${API}/services/${deletingService.service_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        toast.success("Service deleted");
        setShowDeleteModal(false);
        setDeletingService(null);
        fetchServices();
      } else {
        toast.error("Failed to delete service");
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service");
    }
  };

  const toggleStaff = (staffId) => {
    setFormData((prev) => ({
      ...prev,
      staff_ids: prev.staff_ids.includes(staffId)
        ? prev.staff_ids.filter((id) => id !== staffId)
        : [...prev.staff_ids, staffId],
    }));
  };

  const updateAvailability = (dayIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      availability: prev.availability.map((a, i) =>
        i === dayIndex ? { ...a, [field]: value } : a
      ),
    }));
  };

  const formatPrice = (pence) => `£${(pence / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-900">
      {/* Header - Brand Gradient */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/merchant" className="p-2 rounded-lg hover:bg-white/10 text-indigo-100 hover:text-white transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">Services</h1>
              <p className="text-sm text-indigo-100">Manage your service offerings</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/merchant/bookings">
              <Button className="bg-white/20 hover:bg-white/30 text-white border-0">
                <Calendar className="h-4 w-4 mr-2" />
                Bookings
              </Button>
            </Link>
            <Button onClick={openCreateModal} className="bg-white text-indigo-700 hover:bg-indigo-50" data-testid="add-service-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <div className="flex gap-2 border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab("services")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "services"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <LayoutGrid className="h-4 w-4 inline mr-2" />
            Services
          </button>
          <button
            onClick={() => setActiveTab("bundles")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "bundles"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Package className="h-4 w-4 inline mr-2" />
            Bundles
            {bundles.length > 0 && (
              <Badge variant="secondary" className="ml-2">{bundles.length}</Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab("recurring")}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === "recurring"
                ? "text-indigo-600 border-b-2 border-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Repeat className="h-4 w-4 inline mr-2" />
            Recurring
            {recurringBookings.length > 0 && (
              <Badge variant="secondary" className="ml-2">{recurringBookings.length}</Badge>
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "bundles" && (
        <div className="max-w-7xl mx-auto px-4 pb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Service Bundles</h2>
            <p className="text-sm text-gray-500">Prepaid packages with discounts</p>
          </div>
          
          {bundles.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No bundles created yet</p>
              <p className="text-sm text-gray-400 mt-2">Create bundles via API to offer package deals</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bundles.map((bundle) => (
                <div key={bundle.bundle_id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-800">{bundle.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{bundle.description}</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700">
                      <Percent className="h-3 w-3 mr-1" />
                      {bundle.discount_percent}% off
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Gift className="h-4 w-4 mr-2 text-indigo-500" />
                      {bundle.total_sessions} sessions included
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-2 text-indigo-500" />
                      Valid for {bundle.validity_days} days
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-2 text-indigo-500" />
                      {bundle.purchases || 0} purchased
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      <span className="text-sm text-gray-400 line-through">
                        {formatPrice(bundle.original_price)}
                      </span>
                      <span className="text-xl font-bold text-indigo-600 ml-2">
                        {formatPrice(bundle.discounted_price)}
                      </span>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => purchaseBundle(bundle.bundle_id)}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <ShoppingCart className="h-4 w-4 mr-1" />
                      Buy
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "recurring" && (
        <div className="max-w-7xl mx-auto px-4 pb-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Recurring Bookings</h2>
            <p className="text-sm text-gray-500">Automated weekly/monthly appointments</p>
          </div>
          
          {recurringBookings.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Repeat className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No recurring bookings</p>
              <p className="text-sm text-gray-400 mt-2">Set up recurring schedules via API</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recurringBookings.map((rec) => (
                <div key={rec.recurring_id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-800">{rec.service_name}</h3>
                        <Badge variant={rec.status === "active" ? "default" : "secondary"}>
                          {rec.status}
                        </Badge>
                      </div>
                      
                      <div className="grid sm:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Repeat className="h-4 w-4 mr-2 text-indigo-500" />
                          {rec.interval} @ {rec.time}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-indigo-500" />
                          {rec.num_occurrences} sessions
                        </div>
                        <div className="flex items-center">
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                          {rec.completed_count || 0} completed
                        </div>
                      </div>
                      
                      {rec.booking_dates && rec.booking_dates.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {rec.booking_dates.slice(0, 4).map((date, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-indigo-50 text-indigo-600 rounded">
                              {date}
                            </span>
                          ))}
                          {rec.booking_dates.length > 4 && (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">
                              +{rec.booking_dates.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {rec.status === "active" && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => cancelRecurring(rec.recurring_id)}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Services Tab - Original Filters & Grid */}
      {activeTab === "services" && (
        <>
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-gray-200 text-gray-900"
              data-testid="service-search"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory("all")}
              className={selectedCategory === "all" ? "bg-indigo-600 text-white" : "border-gray-200 text-gray-700 hover:bg-gray-100"}
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              All
            </Button>
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.id] || Settings;
              return (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? "bg-indigo-600 text-white" : "border-gray-200 text-gray-700 hover:bg-gray-100"}
                >
                  <Icon className="h-4 w-4 mr-1" />
                  {cat.name}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-16">
            <Settings className="h-16 w-16 mx-auto mb-4 text-slate-600" />
            <h3 className="text-xl font-medium mb-2">No services yet</h3>
            <p className="text-slate-400 mb-6">Create your first service to start taking bookings</p>
            <Button onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Service
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => {
              const Icon = CATEGORY_ICONS[service.category_id] || Settings;
              const colorClass = CATEGORY_COLORS[service.category_id] || "bg-slate-500/20 text-slate-400";
              
              return (
                <div
                  key={service.service_id}
                  className="p-5 rounded-xl bg-white shadow-md border border-gray-100 hover:border-indigo-300 hover:shadow-lg transition-all"
                  data-testid={`service-card-${service.service_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg border ${colorClass}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(service)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDeletingService(service);
                          setShowDeleteModal(true);
                        }}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-1 text-gray-900">{service.name}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4">{service.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {service.duration_minutes}min
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {service.staff?.length || 0} staff
                    </span>
                    {service.is_recurring && (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <RefreshCw className="h-4 w-4" />
                        Recurring
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div>
                      <p className="text-lg font-bold text-indigo-600">{formatPrice(service.base_price)}</p>
                      <p className="text-xs text-gray-500">{service.deposit_percent}% deposit</p>
                    </div>
                    <Badge
                      variant={service.is_active ? "default" : "secondary"}
                      className={service.is_active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-600"}
                    >
                      {service.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </>
      )}

      {/* Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingService ? "Edit Service" : "Create Service"}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingService ? "Update service details" : "Add a new service offering"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-2">Service Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., General Consultation"
                  className="bg-slate-800 border-white/10"
                  data-testid="service-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 block mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the service..."
                  className="bg-slate-800 border-white/10 min-h-[80px]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Category</label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-white/10">
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Duration (minutes)</label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 30 })}
                    min={15}
                    max={480}
                    className="bg-slate-800 border-white/10"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Price (£)</label>
                  <Input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                    min={0}
                    step={0.01}
                    className="bg-slate-800 border-white/10"
                    data-testid="service-price-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Deposit (%)</label>
                  <Input
                    type="number"
                    value={formData.deposit_percent}
                    onChange={(e) => setFormData({ ...formData, deposit_percent: parseInt(e.target.value) || 0 })}
                    min={0}
                    max={100}
                    className="bg-slate-800 border-white/10"
                  />
                </div>
              </div>
            </div>
            
            {/* Staff Assignment */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Assigned Staff</label>
              <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-slate-800/50 border border-white/5 max-h-32 overflow-y-auto">
                {staff.length === 0 ? (
                  <p className="text-slate-500 text-sm col-span-2">No staff available. Add staff first.</p>
                ) : (
                  staff.map((s) => (
                    <label
                      key={s.staff_id}
                      className="flex items-center gap-2 p-2 rounded hover:bg-white/5 cursor-pointer"
                    >
                      <Checkbox
                        checked={formData.staff_ids.includes(s.staff_id)}
                        onCheckedChange={() => toggleStaff(s.staff_id)}
                      />
                      <span className="text-sm">{s.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            
            {/* Recurring Option */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                />
                <span className="text-sm">Recurring Service</span>
              </label>
              
              {formData.is_recurring && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">Repeat every</span>
                  <Input
                    type="number"
                    value={formData.recurring_interval_days || 30}
                    onChange={(e) => setFormData({ ...formData, recurring_interval_days: parseInt(e.target.value) || 30 })}
                    className="w-20 bg-slate-800 border-white/10"
                    min={1}
                  />
                  <span className="text-sm text-slate-400">days</span>
                </div>
              )}
            </div>
            
            {/* Availability */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Availability</label>
              <div className="space-y-2 p-3 rounded-lg bg-slate-800/50 border border-white/5">
                {DAYS.map((day, i) => (
                  <div key={day} className="flex items-center gap-3">
                    <Checkbox
                      checked={formData.availability[i]?.enabled}
                      onCheckedChange={(checked) => updateAvailability(i, "enabled", checked)}
                    />
                    <span className="w-24 text-sm">{day}</span>
                    <Input
                      type="time"
                      value={formData.availability[i]?.start_time || "09:00"}
                      onChange={(e) => updateAvailability(i, "start_time", e.target.value)}
                      disabled={!formData.availability[i]?.enabled}
                      className="w-28 bg-slate-900 border-white/10 text-sm"
                    />
                    <span className="text-slate-500">to</span>
                    <Input
                      type="time"
                      value={formData.availability[i]?.end_time || "17:00"}
                      onChange={(e) => updateAvailability(i, "end_time", e.target.value)}
                      disabled={!formData.availability[i]?.enabled}
                      className="w-28 bg-slate-900 border-white/10 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Advanced Options */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-2">Max Advance Booking (days)</label>
                <Input
                  type="number"
                  value={formData.max_advance_days}
                  onChange={(e) => setFormData({ ...formData, max_advance_days: parseInt(e.target.value) || 30 })}
                  min={1}
                  max={365}
                  className="bg-slate-800 border-white/10"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 block mb-2">Buffer Between Bookings (min)</label>
                <Input
                  type="number"
                  value={formData.buffer_minutes}
                  onChange={(e) => setFormData({ ...formData, buffer_minutes: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={60}
                  className="bg-slate-800 border-white/10"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowServiceModal(false)}
              className="border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-service-btn"
            >
              {saving ? "Saving..." : editingService ? "Update Service" : "Create Service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Service</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete "{deletingService?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesPage;
